// src/components/Navbar/Navbar.tsx

import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Badge,
  Box,
  TextField,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  DoneAll as DoneAllIcon,
  PhotoCamera as PhotoCameraIcon,
  BatteryAlert,
  BatteryChargingFull,
  Battery6Bar,
  Warning,
  SystemUpdateAlt,
  PortableWifiOff,
} from "@mui/icons-material";

import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";

/* ---------- Types ---------- */
interface NotificationType {
  id: string | number;
  type: string;
  message: string;
  time: string;
  read: boolean;
}

interface NavbarProps {
  onMenuToggle?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout, updateAvatarURL } = useAuth();
  const { notifications, markAllAsRead, clearAll, markOneAsRead } =
    useNotifications();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [time, setTime] = useState(new Date());
  const [profileAnchorEl, setProfileAnchorEl] =
    useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] =
    useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tempAvatarPreview, setTempAvatarPreview] =
    useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const h = time.getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  };

  const iconMap: Record<string, { icon: React.ReactNode; color: string }> = {
    "battery-low": { icon: <BatteryAlert />, color: "#EF4444" },
    "battery-medium": { icon: <Battery6Bar />, color: "#E5E7EB" },
    "battery-ok": { icon: <BatteryChargingFull />, color: "#22C55E" },
    alert: { icon: <Warning />, color: "#EF4444" },
    system: { icon: <SystemUpdateAlt />, color: "#3B82F6" },
    "device-disconnected": { icon: <PortableWifiOff />, color: "#EF4444" },
  };

  const handleAvatarUpload = async (file: File) => {
    if (!user) return;

    const previewUrl = URL.createObjectURL(file);
    setTempAvatarPreview(previewUrl);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/avatar/${user._id}`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Upload failed");

      const data = await res.json();
      const avatarPath = data.avatarUrl || data.avatar;

      if (avatarPath) {
        const absolute = new URL(
          avatarPath,
          window.location.origin
        ).pathname;
        updateAvatarURL(absolute);
      }

      setEditDialogOpen(false);
    } catch (err) {
      console.error("Avatar upload failed:", err);
    } finally {
      URL.revokeObjectURL(previewUrl);
      setTempAvatarPreview(null);
      setUploading(false);
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "#273142",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          px: { xs: 2, md: 4 },
        }}
      >
        <Toolbar sx={{ minHeight: 70, justifyContent: "space-between" }}>
          {/* LEFT: MENU + SEARCH */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isMobile && onMenuToggle && (
              <IconButton
                onClick={onMenuToggle}
                sx={{ color: "#e2e8f0" }}
                aria-label="toggle menu"
              >
                <MenuIcon />
              </IconButton>
            )}

            {user && (
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search…"
                size="small"
                sx={{
                  width: 320,
                  bgcolor: "rgba(255,255,255,0.06)",
                  borderRadius: 999,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.12)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.2)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#93C5FD",
                  },
                  "& input": { color: "#e5e7eb" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {/* RIGHT: NOTIFICATIONS + PROFILE */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {user && (
              <>
                <Tooltip title="Notifications">
                  <span>
                    <IconButton
                      onClick={(e) => setNotifAnchorEl(e.currentTarget)}
                      sx={{ color: "#e2e8f0" }}
                      aria-label="notifications"
                    >
                      <Badge
                        badgeContent={
                          notifications.filter((n) => !n.read).length
                        }
                        color="error"
                      >
                        <NotificationsIcon />
                      </Badge>
                    </IconButton>
                  </span>
                </Tooltip>

                <Menu
                  anchorEl={notifAnchorEl}
                  open={Boolean(notifAnchorEl)}
                  onClose={() => setNotifAnchorEl(null)}
                  PaperProps={{
                    sx: {
                      bgcolor: "#273142",
                      color: "#e5e7eb",
                      width: 360,
                      borderRadius: 3,
                      boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom:
                        "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Typography fontSize={14} fontWeight={600}>
                      Notifications
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={markAllAsRead}>
                        <DoneAllIcon
                          sx={{ fontSize: 18, color: "#9ca3af" }}
                        />
                      </IconButton>
                      <IconButton size="small" onClick={clearAll}>
                        <LogoutIcon
                          sx={{ fontSize: 18, color: "#9ca3af" }}
                        />
                      </IconButton>
                    </Stack>
                  </Box>

                  {notifications.length === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        color: "#9ca3af",
                      }}
                    >
                      <Typography variant="body2">
                        No new notifications
                      </Typography>
                    </Box>
                  ) : (
                    notifications.map((n: NotificationType) => {
                      const meta = iconMap[n.type] || {
                        icon: <NotificationsIcon />,
                        color: "#9CA3AF",
                      };

                      return (
                        <Box
                          key={n.id}
                          onClick={() =>
                            markOneAsRead(Number(n.id))
                          }
                          sx={{
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            gap: 1.5,
                            cursor: "pointer",
                            opacity: n.read ? 0.6 : 1,
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,0.05)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: `${meta.color}33`,
                              color: meta.color,
                            }}
                          >
                            {meta.icon}
                          </Box>
                          <Box>
                            <Typography fontSize={13}>
                              {n.message}
                            </Typography>
                            <Typography
                              fontSize={11}
                              color="rgba(255,255,255,0.6)"
                            >
                              {n.time}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Menu>
              </>
            )}

            {!isMobile && user && (
              <Box textAlign="right">
                <Typography fontSize={13} color="#e5e7eb">
                  {time.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  EAT
                </Typography>
                <Typography fontSize={11} color="#9ca3af">
                  {getGreeting()}, {user.email.split("@")[0]}
                </Typography>
              </Box>
            )}

            {user && (
              <>
                <Avatar
                  src={tempAvatarPreview || user.avatar || undefined}
                  onClick={(e) => setProfileAnchorEl(e.currentTarget)}
                  sx={{
                    width: 36,
                    height: 36,
                    cursor: "pointer",
                    "&:hover": { opacity: 0.85 },
                  }}
                />

                <Menu
                  anchorEl={profileAnchorEl}
                  open={Boolean(profileAnchorEl)}
                  onClose={() => setProfileAnchorEl(null)}
                  PaperProps={{
                    sx: { bgcolor: "#273142", color: "#e5e7eb" },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setEditDialogOpen(true);
                      setProfileAnchorEl(null);
                    }}
                  >
                    <EditIcon sx={{ mr: 1.5 }} />
                    Edit Profile
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      logout();
                      navigate("/login");
                    }}
                  >
                    <LogoutIcon sx={{ mr: 1.5 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Edit Profile Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "#273142",
            color: "#e5e7eb",
            borderRadius: 3,
            width: 380,
          },
        }}
      >
        <DialogTitle>Edit Profile</DialogTitle>

        <DialogContent sx={{ textAlign: "center", pt: 2 }}>
          <Avatar
            src={tempAvatarPreview || user?.avatar || undefined}
            sx={{ width: 120, height: 120, mx: "auto", mb: 2 }}
          />

          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCameraIcon />}
            disabled={uploading}
          >
            {uploading ? (
              <CircularProgress size={16} />
            ) : (
              "Change Avatar"
            )}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(e) =>
                e.target.files?.[0] &&
                handleAvatarUpload(e.target.files[0])
              }
            />
          </Button>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
